import React, { useContext, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { FiDownload } from "react-icons/fi";
import AuthContext from "../../context/AuthContext.js";
import DownloadRulesPDF from "../../utils/DownloadRulesPDF.js";

const RulesDialog = ({ isOpen, onClose, data, loading }) => {
  const { selectedSite, selectedSiteName } = useContext(AuthContext);
  const [downloadLoading, setDownloadLoading] = useState(false);

  if (!isOpen) return null;

  const rules = data?.rules || [];
  const train = data?.train || {};

  const handleDownload = () => {
    DownloadRulesPDF(selectedSiteName, train, rules, setDownloadLoading);
  };

  const formatRange = (min, max) => {
    if (min === null || max === null) {
      if (min === null && max === null) return "N/A";
      return `${min ?? max} °C`;
    }
    if (min === max) return `${min} °C`;
    return `${min} - ${max} °C`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden transform animate-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 px-6 py-4 flex justify-between items-center text-white">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Alarm & Warning Rules</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              className="p-1.5 hover:bg-white/10 rounded-full transition-all text-white/80 hover:text-white"
              aria-label="Download PDF"
              disabled={downloadLoading}
            >
              {downloadLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <FiDownload size={22} />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded-full transition-all text-white/80 hover:text-white"
              aria-label="Close dialog"
            >
              <RxCross2 size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-gray-500 text-sm font-medium animate-pulse tracking-wide">Fetching data...</p>
            </div>
          ) : (
            <>
              {/* Rules Table */}
              <div className="relative">

                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-[10px] font-black text-gray-500 uppercase tracking-wider">Coach Type</th>
                        <th className="px-4 py-3 text-center text-[10px] font-black text-gray-500 uppercase tracking-wider">Temp Range</th>
                        <th className="px-4 py-3 text-center text-[10px] font-black text-gray-500 uppercase tracking-wider">Diff Range</th>
                        <th className="px-4 py-3 text-center text-[10px] font-black text-gray-500 uppercase tracking-wider">Alarm Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rules.length > 0 ? (
                        rules.map((rule, idx) => (
                          <tr key={idx} className="hover:bg-blue-50/30 transition-all duration-150">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600">
                                  {rule.TypeOfBogi?.charAt(0) || "S"}
                                </div>
                                <span className="text-xs font-bold text-gray-900 tracking-tight">{rule.TypeOfBogi || "Standard"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center whitespace-nowrap">
                              <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                                {formatRange(rule.MinTemp, rule.MaxTemp)}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center whitespace-nowrap">
                              <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                                {formatRange(rule.MinDiff, rule.MaxDiff)}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: rule.SeverityColor || '#10b981' }}></span>
                                <span className="text-[10px] font-black text-gray-800 uppercase tracking-widest">
                                  {!rule.SeverityColor ? 'Safe' : (
                                    rule.SeverityColor.toLowerCase() === 'red' ? 'Alarm' : (
                                      (rule.SeverityColor.toLowerCase() === 'yellow' || rule.SeverityColor.toLowerCase() === 'orange') ? 'Warning' : 'Match'
                                    )
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-300">No Logic Applied</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50/80 backdrop-blur-md px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-900 hover:bg-black text-white text-[10px] font-black uppercase tracking-widest rounded-lg transition-all shadow-md active:scale-95 disabled:opacity-50"
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RulesDialog;
